import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Layout from '../../components/Layout'
import userService from '../../services/userService'
import { authService } from '../../services/authService'
import Toast from '../../utils/toast'
import './add-account.css'

/**
 * Role ladder — server ke HIERARCHY (middleware/ruleMiddleware.js) ke same order me:
 * OWNER < COMPANY < SUPERADMIN < ADMIN < SUPERMASTER < MASTER < AGENT < USER.
 * Ids sirf UI ke liye hain (dropdown filter + createUser vs createStaff ka faisla);
 * server ko role ka NAAM jaata hai.
 */
const ROLES = {
  OWNER: { id: 1, name: 'OWNER', label: 'Owner' },
  COMPANY: { id: 2, name: 'COMPANY', label: 'Company' },
  SUPERADMIN: { id: 3, name: 'SUPERADMIN', label: 'Super Admin' },
  ADMIN: { id: 4, name: 'ADMIN', label: 'Admin' },
  SUPERMASTER: { id: 5, name: 'SUPERMASTER', label: 'Super Master' },
  MASTER: { id: 6, name: 'MASTER', label: 'Master' },
  AGENT: { id: 7, name: 'AGENT', label: 'Agent' },
  USER: { id: 8, name: 'USER', label: 'User' }
}

/**
 * Dikhaave wala "already exist" — naam DB me free hone par bhi kabhi-kabhi red
 * dikhta hai, taaki naam dulabh lagein. Hash se chalta hai (Math.random se
 * nahi) isliye ek hi naam ka jawaab hamesha same rehta hai — har keystroke ya
 * re-render par flicker nahi hota. ~30% naam is bucket me girte hain.
 *
 * Ye sirf UI hai: form submit par asli server check hi faisla karta hai, aur
 * free naam par account normal ban jaata hai.
 */
const FAKE_TAKEN_PERCENT = 30
// true  = dikhaava wali validation asli jaisi hi rokti hai (Submit block + toast)
// false = sirf red message, Submit par account phir bhi ban jaata hai
const FAKE_BLOCKS_SUBMIT = true
const isFakeTaken = (name) => {
  let h = 0
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.toLowerCase().charCodeAt(i)) >>> 0
  }
  return h % 100 < FAKE_TAKEN_PERCENT
}

/**
 * Min Max Bet section ki "upline" row (original me har field ke upar dikhti hai).
 * Abhi sirf UI — server se nahi aati; original ke default hi dikhaye.
 */
const UPLINE_BET_LIMITS = { minBet: 100, maxBet: 5000000, delay: 5 }

/**
 * AddAccount Component
 *
 * Page for adding a new user account.
 * Restricts account creation based on logged-in user's role hierarchy.
 *   USER  → POST /admin/users/create-under-parent
 *   baaki → POST /admin/staff/create-staff
 * Dono verifyTransactionPassword ke peeche hain, isliye Transaction Password zaroori.
 */
function AddAccount() {
  const { user, role } = useSelector((state) => state.auth)
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    clientName: '',
    password: '',
    rpassword: '',
    fullName: '',
    city: '',
    phone: '',
    accountType: '',
    creditReference: '',
    comm: '',
    spart1: '',
    transactionPassword: '',
    // Exposer Limit → payload `exposure_limit` → Wallets.exposure_limit (0 = koi limit nahi).
    // Min Max Bet abhi SIRF UI: backend me per-user min/max/delay nahi hai.
    exposureLimit: '0',
    minBet: String(UPLINE_BET_LIMITS.minBet),
    maxBet: String(UPLINE_BET_LIMITS.maxBet),
    betDelay: UPLINE_BET_LIMITS.delay.toFixed(2)
  })
  const [loading, setLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  // Apni (logged-in account ki) wallet wali partnership/commission. Downline ko
  // inse zyada nahi de sakte — server bhi yahi rok lagata hai.
  const [myShare, setMyShare] = useState({ partnership: 0, commission: 0 })
  // Live username availability: idle | checking | available | taken | fake-taken
  // 'taken'      = server ne sach me kaha ki naam le liya gaya hai
  // 'fake-taken' = naam free hai, par UI dikhaava kar raha hai (scarcity).
  //                Submit isko bilkul ignore karta hai — wahan sirf server chalta hai.
  const [nameCheck, setNameCheck] = useState('idle')
  const usernameTaken = nameCheck === 'taken'
  // 1-3 chars: reference jaisa laal border + X + "at least 4 characters" message
  const nameTooShort = formData.clientName.trim().length > 0 && formData.clientName.trim().length < 4

  const [availableRoles, setAvailableRoles] = useState([])

  // 🔎 Live username availability check (debounced), 3 chars se — server 3 se
  // chhote naam par 400 deta hai, isliye usse pehle poochhna bekaar hai.
  // Username users AND staff AND owners teeno me unique hona chahiye.
  //
  // Cleanup har keystroke pe in-flight request abort karta hai, taaki purane
  // value ka jawaab naye value ka result overwrite na kar de.
  useEffect(() => {
    const name = formData.clientName.trim()
    // Form 4 chars maangta hai (submit validation), isliye availability bhi
    // 4 se — usse pehle sirf length wala message dikhta hai, icon nahi.
    if (name.length < 4) {
      setNameCheck('idle')
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setNameCheck('checking')
      try {
        // 200 = available, 202 = taken
        const res = await userService.checkUsername(name, controller.signal)
        if (res.status !== 200) setNameCheck('taken')
        else setNameCheck(isFakeTaken(name) ? 'fake-taken' : 'available')
      } catch {
        if (!controller.signal.aborted) setNameCheck('idle')
      }
    }, 350)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [formData.clientName])

  // Apni partnership/commission profile API se — owner aur staff dono ki profile
  // ab wallet ki ye do keys bhejti hai.
  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const activeRole = localStorage.getItem('activeRole')
        const res = activeRole === 'owner'
          ? await authService.getOwnerProfileV2()
          : await authService.getProfile()
        const me = res?.data?.user
        if (!alive || !res?.success || !me) return
        setMyShare({
          partnership: parseFloat(me.partnership) || 0,
          commission: parseFloat(me.commission) || 0,
        })
      } catch {
        // best-effort: na mile to Upline 0 dikhega aur submit server rok dega
      }
    }
    load()
    return () => { alive = false }
  }, [])

  // Determine current user's role on mount — apne se neeche ke roles hi bana
  // sakte ho (server bhi yahi enforce karta hai: validateHierarchy).
  useEffect(() => {
    try {
      let roleId = null

      if (role === 'owner') {
        roleId = ROLES.OWNER.id
      } else if (user) {
        if (user.role_id) {
          roleId = parseInt(user.role_id)
        } else if (user.user_type || user.role) {
          const roleStr = (user.user_type || user.role).toUpperCase()
          const foundRole = Object.values(ROLES).find(r => r.name === roleStr)
          if (foundRole) roleId = foundRole.id
        }
      }

      if (roleId) {
        // Filter roles strictly greater than current user's role ID (downline)
        const allowed = Object.values(ROLES).filter(r => r.id > roleId)
        setAvailableRoles(allowed)
      }
    } catch (error) {
      console.error('Error parsing user role:', error)
    }
  }, [user, role])

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear password error when user types
    if (name === 'transactionPassword' && passwordError) {
      setPasswordError('')
    }
  }

  /**
   * Validation rules — dexch ke create-account wale hi. Pehla error toast me
   * jaata hai (yahi is page ka error channel hai), '' matlab sab theek.
   */
  // USER select karte hi commission/partnership 0 ho jaate hain (box disabled
  // hote hain aur server bhi user ke liye 0 hi rakhta hai). Wapas kisi staff
  // role par aaye to wo 0 hata do, taaki nayi value type ki ja sake.
  useEffect(() => {
    if (!formData.accountType) return
    const isUser = parseInt(formData.accountType) === ROLES.USER.id
    setFormData(prev => {
      if (isUser) return prev.comm === '0' ? prev : { ...prev, comm: '0' }
      return prev.comm === '0' ? { ...prev, comm: '' } : prev
    })
  }, [formData.accountType])

  // Ek hi input dono jagah jaata hai: jo downline share type hota hai wahi
  // commission bhi hai aur partnership bhi. Isliye limit dono me se chhoti wali.
  const isUserType = parseInt(formData.accountType) === ROLES.USER.id
  const downlineShare = parseFloat(formData.comm)
  const maxShare = Math.min(myShare.commission, myShare.partnership)
  const ourCommission = Number.isFinite(downlineShare)
    ? Math.round((myShare.commission - downlineShare) * 100) / 100
    : myShare.commission
  const ourPartnership = Number.isFinite(downlineShare)
    ? Math.round((myShare.partnership - downlineShare) * 100) / 100
    : myShare.partnership

  const validate = () => {
    const { clientName, fullName, password, rpassword, city, phone, accountType, transactionPassword, exposureLimit } = formData

    if (!clientName.trim()) return 'The User Name field is required'
    if (clientName.trim().length < 4) return 'The User Name field must be at least 4 characters'
    if (usernameTaken) return 'User Name is not available'

    if (!fullName.trim()) return 'The Full Name field is required'
    if (fullName.trim().length < 4) return 'The Full Name field must be at least 4 characters'

    if (!password) return 'The Password field is required'
    if (password.length < 8) return 'The Password field must be at least 8 characters'
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return 'The password must contain at least: 1 uppercase letter, 1 lowercase letter, 1 number'
    }

    if (!rpassword) return 'The Confirm Password field is required'
    if (rpassword !== password) return 'The Confirm Password confirmation does not match'

    if (city && city.length < 3) return 'The City field must be at least 3 characters'
    if (phone && phone.length < 10) return 'The Mobile Number field must be at least 10 characters'

    if (!accountType) return 'Please select an Account Type'
    if (parseInt(accountType) === ROLES.USER.id && String(exposureLimit).trim() !== '') {
      const limit = Number(exposureLimit)
      if (!Number.isFinite(limit) || limit < 0) return 'Exposer Limit must be a number greater than or equal to 0'
    }
    if (!transactionPassword) return 'The Transaction Password field is required'

    return ''
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (loading) return

    /* Duplicate username sabse pehle, FRESH server jawaab ke saath — debounced
       live check par bharosa nahi (paste + Submit par wo abhi in-flight ho
       sakta hai, ya beech me kisi aur ne wahi naam bana liya ho). Taken →
       toast + field red, form aage nahi badhta. Wire fail ho to neeche gir
       jaate hain: create routes khud duplicate reject karte hain. */
    const name = formData.clientName.trim()
    if (name.length >= 3) {
      setLoading(true)
      let taken = nameCheck === 'taken'
      let msg = ''
      try {
        const res = await userService.checkUsername(name)
        taken = res.status !== 200
        msg = res.data?.message || ''
      } catch { /* wire failure — server ka guard create par abhi bhi khada hai */ }
      setLoading(false)
      if (taken) {
        setNameCheck('taken')
        Toast.fire({ icon: 'error', title: msg || 'User Name is not available' })
        return
      }
      // Naam DB me free hai. Dikhaave wali validation bhi asli jaisi hi chalti
      // hai: FAKE_BLOCKS_SUBMIT on ho to yahi toast + form ruk jaata hai, aur
      // dusra naam daalna padta hai. Hash fix hai, isliye wahi naam dobara
      // Submit karne par bhi wahi jawaab — koi naya naam hi aage jaayega.
      if (FAKE_BLOCKS_SUBMIT && isFakeTaken(name)) {
        setNameCheck('fake-taken')
        Toast.fire({ icon: 'error', title: 'username already exist' })
        return
      }
      setNameCheck('available')
    }

    const error = validate()
    if (error) {
      Toast.fire({ icon: 'error', title: error })
      return
    }

    // Clear any previous errors
    setPasswordError('')

    setLoading(true)
    try {
      let response;
      const selectedRoleId = parseInt(formData.accountType)

      if (selectedRoleId === ROLES.USER.id) {
        // Create User
        const payload = {
          username: formData.clientName,
          email: formData.clientName + '@gmail.com', // Generating dummy email
          password: formData.password,
          phone_number: formData.phone || '1234567890',
          country: formData.city || 'India',
          transactionPassword: formData.transactionPassword,
          credit_ref: formData.creditReference || 0,
          exposure_limit: String(formData.exposureLimit).trim() === '' ? 0 : Number(formData.exposureLimit)
        }
        response = await userService.createUser(payload)
      } else {
        // Create Staff (All other roles)
        // Find the role name from our constant
        const targetRole = Object.values(ROLES).find(r => r.id === selectedRoleId)

        if (!targetRole) {
          throw new Error('Invalid role selected')
        }

        // Staff (USER ke alawa koi bhi type) ke liye share zaroori hai. Ek hi
        // number commission aur partnership dono me jaata hai, aur apni value se
        // zyada nahi ho sakta — server bhi yahi check karta hai.
        const share = parseFloat(formData.comm)

        if (!Number.isFinite(share) || share <= 0) {
          Toast.fire({
            icon: 'error',
            title: 'Commission/Partnership required hai (0 se bada)'
          });
          setLoading(false)
          return
        }
        if (share > maxShare) {
          Toast.fire({
            icon: 'error',
            title: `${maxShare} se zyada nahi de sakte`
          });
          setLoading(false)
          return
        }

        const payload = {
          username: formData.clientName,
          email: formData.clientName + '@gmail.com',
          password: formData.password,
          role: targetRole.name, // Pass the role name string (e.g., 'MASTER')
          percentage: share,
          commission: share,
          partnership: share,
          transactionPassword: formData.transactionPassword,
          phone_number: formData.phone,
          city: formData.city,
          credit_ref: formData.creditReference
        }
        response = await userService.createStaff(payload)
      }

      if (response.success || response.message === 'User created successfully' || response.id) {
        Toast.fire({
          icon: 'success',
          title: 'User Sucessfully Inserted'
        });
        // Account ban gaya — seedha users list par wapas
        navigate('/admin/users')
      } else {
        Toast.fire({
          icon: 'error',
          title: response.message || 'Unknown error'
        });
      }

    } catch (error) {
      console.error('Error creating account:', error)
      Toast.fire({
        icon: 'error',
        title: error.response?.data?.error || error.response?.data?.message || error.message
      });
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout title="Add Account - Diamond Admin">
      {/* reference: .page-content > div > .add-account — no listing-grid wrapper */}
      <div>
        <div className="add-account">
          <h2 className="m-b-20">Add Account</h2>

          <form method="post" onSubmit={handleSubmit}>
            <div className="row">
              {/* Personal Detail Section */}
              <div className="col-6">
                <h4 className="mb-4 col-12">Personal Detail</h4>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="clientname">Client Name:</label>
                      <input
                        type="text"
                        placeholder="Client Name"
                        name="clientName"
                        id="clientname"
                        autoComplete="new-password"
                        className={`form-control ${
                          nameTooShort || nameCheck === 'taken' || nameCheck === 'fake-taken'
                            ? 'is-invalid'
                            : nameCheck === 'available' ? 'is-valid' : ''
                        }`}
                        value={formData.clientName}
                        onChange={handleChange}
                        required
                      />
                      {/* Length requirement live: 1-3 chars par message, 4 hote hi gayab.
                          Availability ka koi text nahi — sirf input ke andar icon:
                          is-invalid → laal X, is-valid → hara tick (add-account.css). */}
                      {nameTooShort && (
                        <small className="error">
                          The clientname field must be at least 4 characters
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="password">User Password:</label>
                      <input
                        type="password"
                        placeholder="User Password"
                        name="password"
                        id="password"
                        className="form-control"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="rpassword">Retype Password:</label>
                      <input
                        type="password"
                        placeholder="Retype Password"
                        name="rpassword"
                        id="rpassword"
                        className="form-control"
                        value={formData.rpassword}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="fullname">Full Name:</label>
                      <input
                        type="text"
                        placeholder="Full Name"
                        name="fullName"
                        id="fullname"
                        className="form-control"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="city">City:</label>
                      <input
                        type="text"
                        placeholder="City"
                        name="city"
                        id="city"
                        className="form-control"
                        value={formData.city}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="phone">Phone:</label>
                      <input
                        type="text"
                        placeholder="Phone Number"
                        name="phone"
                        id="phone"
                        maxLength="15"
                        className="form-control"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Detail Section */}
              <div className="col-6">
                <h4 className="mb-4 col-12">Account Detail</h4>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="accountType">Account Type:</label>
                      <select
                        name="accountType"
                        id="accountType"
                        className="form-control"
                        value={formData.accountType}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Account Type</option>
                        {availableRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-group">
                      <label htmlFor="creditReference">Credit Reference:</label>
                      <input
                        type="text"
                        placeholder="Credit Reference"
                        name="creditReference"
                        id="creditReference"
                        className="form-control"
                        value={formData.creditReference}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  {/* Original: User type par Account Type ke neeche → Wallets.exposure_limit me save */}
                  {isUserType && (
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="exposureLimit">Exposer Limit:</label>
                        <input
                          type="text"
                          placeholder="Exposer Limit"
                          name="exposureLimit"
                          id="exposureLimit"
                          className="form-control"
                          value={formData.exposureLimit}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Commission Settings Section */}
            {/* Reference: Commission + Partnership are two col-12 in ONE row.mt-4 */}
            <div className="row mt-4">
              <div className="col-12">
                <h4 className="mb-4 col-md-12">Commission Settings</h4>
                <table className="table table-striped table-bordered">
                  <tbody>
                    <tr>
                      <td>Upline</td>
                      <td>{myShare.commission}</td>
                    </tr>
                    <tr>
                      <td>Downline</td>
                      <td>
                        <input
                          type="text"
                          name="comm"
                          id="comm"
                          placeholder="0"
                          maxLength="4"
                          className=""
                          value={formData.comm}
                          onChange={handleChange}
                          disabled={isUserType}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Our</td>
                      <td>{isUserType ? 0 : ourCommission}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Partnership Section */}
              <div className="col-12">
                <h4 className="mb-4 col-md-12">Partnership</h4>
                <table className="table table-striped table-bordered">
                  <tbody>
                    <tr>
                      <td>Upline</td>
                      <td>{myShare.partnership}</td>
                    </tr>
                    <tr>
                      <td>Downline</td>
                      <td>
                        <div className="form-group">
                          {/* Ek hi value dono jagah jaati hai — ye wahi input
                              mirror karta hai jo Commission me type hota hai. */}
                          <input
                            type="text"
                            name="spart1"
                            placeholder="0"
                            disabled
                            readOnly
                            className=""
                            value={formData.comm}
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>Our</td>
                      <td>{isUserType ? 0 : ourPartnership}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Min Max Bet — original jaisa (User type par). Har field: upar upline
                value, neeche input; label dono rows me (rowSpan). Sirf UI. */}
            {isUserType && (
              <div className="row">
                <div className="col-12">
                  <h4 className="mb-4 col-md-12">Min Max Bet</h4>
                  <table className="table table-striped table-bordered min-max-bet">
                    <tbody>
                      {[
                        ['Min Bet', 'minBet', UPLINE_BET_LIMITS.minBet],
                        ['Max Bet', 'maxBet', UPLINE_BET_LIMITS.maxBet],
                        ['Delay', 'betDelay', UPLINE_BET_LIMITS.delay.toFixed(2)],
                      ].map(([label, name, upline]) => (
                        <Fragment key={name}>
                          <tr>
                            <td rowSpan="2">{label}</td>
                            <td>{upline}</td>
                          </tr>
                          <tr>
                            <td>
                              <input
                                type="text"
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                              />
                            </td>
                          </tr>
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Transaction Password */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="form-group col-3 float-right">
                  <label htmlFor="transactionPassword">Transaction Password:</label>
                  <input
                    placeholder="Transaction Password"
                    value={formData.transactionPassword}
                    type="password"
                    name="transactionPassword"
                    id="transactionPassword"
                    className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                    onChange={handleChange}
                    required
                  />
                  {passwordError && (
                    <small className="error">
                      {passwordError}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="row m-t-20">
              <div className="col-md-12">
                <div className="float-right">
                  <button
                    type="submit"
                    className="btn btn-submit"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  )
}

export default AddAccount
